import { ApolloLink } from "@apollo/client";
import { ApolloClient, InMemoryCache } from "@apollo/client/core";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { createApolloProvider } from "@vue/apollo-option";
import { logErrorMessages } from "@vue/apollo-util";
import { ErrorLink } from "apollo-link-error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { BEFORE_SILENT_SIGN_IN_ATTEMPT } from "~/constants/sessionStatuses";
import { API_URL, SESSION_TOKEN } from "~/constants/settings";
import { store } from "~/store";
import { captureGraphQLError, isValidationOnly } from "~/utils/captureGraphQLError";
import { dayjs } from "~/utils/dayjs";
import { isRedundantSignOut, isUnauthenticated } from "~/utils/gqlHelpers";
import { setNotFound } from "~/utils/notFound";
import showUpdateMessage from "~/utils/showUpdateMessage";

const queryLink = new BatchHttpLink({
  batchMax: 10,
  batchInterval: 10,
  uri: `${API_URL}/graphql`,
  credentials: "include",
  headers: { batch: "true" },
});

const mutationLink = createUploadLink({
  uri: `${API_URL}/graphql`,
  credentials: "include",
  headers: { "Apollo-Require-Preflight": "true" },
});

const httpLink = ApolloLink.split(
  (operation) => {
    return !operation.query.definitions.find((it) => it.operation === "mutation");
  },
  queryLink,
  mutationLink
);

const authMiddleware = new ApolloLink((operation, forward) => {
  const jwtToken = localStorage.getItem(SESSION_TOKEN);
  const headers = { Authorization: `Bearer ${jwtToken}` };
  const institutionNumber = window.location.pathname.match(/^\/(\d+)/)?.[1];

  if (institutionNumber) {
    headers.Institution = institutionNumber;
  }

  if (jwtToken) {
    operation.setContext({ headers });
  }
  return forward(operation);
});

/**
 * Formats all Date objects in the query variables to ISO 8601 strings and keeps the timezone.
 * If we let apollo client handle this, the date is converted to UTC and the date could be off.
 * The Ruby GraphQL will just strip the timezone and use the date as is.
 *
 * Example using new Date('2024-01-10'):
 * Apollo Client: 2024-01-09T23:00:00.000Z
 * This middleware: 2024-01-10T00:00:00+01:00
 */
const dateFormattingMiddleware = new ApolloLink((operation, forward) => {
  Object.entries(operation.variables).forEach(([key, value]) => {
    if (value instanceof Date) {
      operation.variables[key] = dayjs(value).format();
    }
  });

  return forward(operation);
});

const versionAfterware = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const context = operation.getContext();
    const version = context.response.headers.get("Frontend-Version");

    if (
      version &&
      __FRONTEND_VERSION__ && // eslint-disable-line no-undef
      ![__FRONTEND_VERSION__, "development"].includes(version) // eslint-disable-line no-undef
    ) {
      showUpdateMessage(__FRONTEND_VERSION__); // eslint-disable-line no-undef
    }

    return response;
  })
);

const errorLink = new ErrorLink((error) => {
  const isGraphQLError = Boolean(error.graphQLErrors && error.graphQLErrors.length);
  const isNetworkError = Boolean(error.networkError);
  // A validation error next to a crash must not hide the crash, so only an all-validation response is skipped.
  const isValidationErrors = isGraphQLError && isValidationOnly(error.graphQLErrors);
  const isSilentSignInError =
    isGraphQLError && error.graphQLErrors.some((e) => ["invalid_refresh_token", "no_refresh_token", "unilogin_expired"].includes(e.extensions?.code));
  const isLoginError = isGraphQLError && error.graphQLErrors.some((e) => ["invalid_credentials", "local_login_disabled"].includes(e.extensions?.code));
  const isBlockedError = isGraphQLError && error.graphQLErrors.some((e) => e.message === "blokeret");
  const isNotFoundError = isGraphQLError && error.graphQLErrors.some((e) => e.extensions?.code === "NOT_FOUND_ERROR");
  // A role check refusing the user is not a bug; the backend does not report it either.
  const isUnauthorizedError = isGraphQLError && error.graphQLErrors.every((e) => e.extensions?.code === "UNAUTHORIZED_ERROR");
  const isUnauthenticatedError = isUnauthenticated(error);

  if (isNetworkError && error.networkError.statusCode === 404) {
    return setNotFound();
  }

  // Browsers throttle the timers of a backgrounded tab, so the scheduled token renewal
  // can fire long after the token expired and every query on the page comes back
  // unauthenticated at once. refreshSession shares one request between all callers,
  // so asking per error is safe; it chooses between a silent renewal and the
  // expired-session screen.
  //
  // Not before the first attempt, though: route guards query before LogInBarrier
  // mounts, and renewing here would settle the session before the barrier can
  // watch it change — leaving a logged-out visitor on a white page.
  // A refused signOut already reached the state it wanted, so it must not start a renewal either.
  if (isUnauthenticatedError && !isRedundantSignOut(error) && store.getters["session/status"] !== BEFORE_SILENT_SIGN_IN_ATTEMPT) {
    store.dispatch("session/refreshSession");
  }

  const shouldNotifyError =
    !isNetworkError &&
    !isValidationErrors &&
    !isSilentSignInError &&
    !isLoginError &&
    !isBlockedError &&
    !isNotFoundError &&
    !isUnauthorizedError &&
    !isUnauthenticatedError &&
    !isRedundantSignOut(error);

  if (!import.meta.env.PROD || shouldNotifyError) {
    logErrorMessages(error);
    captureGraphQLError(error);
  }
});

const cache = new InMemoryCache({
  possibleTypes: {
    EducationModuleInterface: ["Graduation", "VocationalTraining", "WorkExperience", "Workshop", "Course", "OptionalSubject"],
  },
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([versionAfterware, dateFormattingMiddleware, authMiddleware, errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

export const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
});
